import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  Divider,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  Chat as ChatIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  SmartToy as BotIcon
} from '@mui/icons-material';
import { getChatHistoryStats, clearChatHistory, getRecentActivity } from './chatHistoryStorage.js';

export default function ChatHistoryManagement() {
  const [stats, setStats] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [recentActivity, setRecentActivity] = useState(null);

  const refreshStats = () => {
    const currentStats = getChatHistoryStats();
    const activity = getRecentActivity(7);
    setStats(currentStats);
    setRecentActivity(activity);
  };

  useEffect(() => {
    refreshStats();
  }, []);

  const handleClearHistory = () => {
    const cleared = clearChatHistory();
    setClearConfirmOpen(false);
    refreshStats();
    
    // Show success message (you could use a snackbar here)
    console.log(`Cleared ${cleared} chat messages`);
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return date.toLocaleDateString();
  };

  const formatDateTime = (date) => {
    if (!date) return 'Unknown';
    return date.toLocaleString();
  };

  if (!stats) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading chat history statistics...</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ padding: 3, marginBottom: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'medium' }}>
        <ChatIcon />
        Chat History Management
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>What is chat history?</strong>
        </Typography>
        <Typography variant="body2">
          Your chat conversations with AI models are stored locally in your browser to provide context for follow-up questions. 
          This includes both your messages and AI responses, helping maintain conversation continuity.
        </Typography>
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {stats.totalMessages}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Messages
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main">
                  {stats.userMessages}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <PersonIcon fontSize="small" />
                  Your Messages
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="info.main">
                  {stats.botMessages}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <BotIcon fontSize="small" />
                  AI Responses
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {stats.storageSizeKB} KB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Storage Used
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Token Statistics */}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h5" color="primary">
                  {stats.totalTokens.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Tokens (estimated)
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h5" color="success.main">
                  {stats.userTokens.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your Tokens
                </Typography>
                {stats.averageUserTokensPerMessage > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ~{stats.averageUserTokensPerMessage} per message
                  </Typography>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h5" color="info.main">
                  {stats.botTokens.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AI Tokens
                </Typography>
                {stats.averageBotTokensPerMessage > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ~{stats.averageBotTokensPerMessage} per message
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Recent Activity */}
          {recentActivity && recentActivity.totalMessages > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box textAlign="center">
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Last 7 Days Activity
                </Typography>
                <Typography variant="body2">
                  {recentActivity.totalMessages} messages ({recentActivity.userMessages} yours, {recentActivity.botMessages} AI responses)
                </Typography>
              </Box>
            </>
          )}

          {/* Date Range */}
          {stats.oldestMessage && stats.newestMessage && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box textAlign="center">
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Chat History Range
                </Typography>
                <Typography variant="body2">
                  {formatDate(stats.oldestMessage)} - {formatDate(stats.newestMessage)}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1, mb: 0 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refreshStats}
        >
          Refresh Stats
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<InfoIcon />}
          onClick={() => setDetailsOpen(true)}
          disabled={stats.totalMessages === 0}
        >
          View Details
        </Button>
        
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setClearConfirmOpen(true)}
          disabled={stats.totalMessages === 0}
        >
          Clear History
        </Button>
      </Box>

      {/* Chat History Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Chat History Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Detailed breakdown of your {stats.totalMessages} stored chat messages.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Message Breakdown</Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={`Your Messages: ${stats.userMessages}`}
                    secondary={`${stats.userTokens.toLocaleString()} tokens (~${stats.averageUserTokensPerMessage} avg)`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`AI Responses: ${stats.botMessages}`}
                    secondary={`${stats.botTokens.toLocaleString()} tokens (~${stats.averageBotTokensPerMessage} avg)`}
                  />
                </ListItem>
              </List>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Storage Information</Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={`Storage Size: ${stats.storageSizeKB} KB`}
                    secondary={`${stats.storageSize} bytes`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`Date Range`}
                    secondary={stats.oldestMessage && stats.newestMessage ? 
                      `${formatDateTime(stats.oldestMessage)} - ${formatDateTime(stats.newestMessage)}` : 
                      'No timestamps available'
                    }
                  />
                </ListItem>
              </List>
            </Grid>
            
            {stats.uniqueLLMs.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>AI Models Used</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {stats.uniqueLLMs.map((llm, index) => (
                    <Chip 
                      key={index} 
                      label={llm.replace('/api/toolchat/', '').replace('/api/openaichat/', '')} 
                      size="small" 
                      variant="outlined" 
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Clear History Confirmation Dialog */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
      >
        <DialogTitle>Clear Chat History</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear all chat history? 
            This will remove {stats.totalMessages} messages ({stats.storageSizeKB} KB) 
            and cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>What happens next:</strong> Your chat conversations will start fresh, 
            and AI responses won't have context from previous conversations until you build up new history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleClearHistory} color="error" variant="contained">
            Clear History
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
